import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import DotWaveLoader from '../../components/commonComponents/DotWaveLoader';
import KitChip from '../../components/ui/Chip';
import { performancePanelSx } from '../../styles/performanceMsiStyles';
import { sectionTitleSx } from '../../styles/typography';
import { isHistoryContextConflict } from '../currentAttribution';
import CalendarCard from './CalendarCard';
import { cellOf, cellValue, pathPoints, pathSummary, waterfallSteps } from './exploreColumns';
import { cellNotes, formatCompactUsd, formatDay, formatPercent, formatRange, formatUsd, formatWindow, reasonsText } from './exploreFormat';
import FactorWaterfall from './FactorWaterfall';
import ReplayPathChart from './ReplayPathChart';

const panelSx = { p: 1.5, display: 'grid', gap: 1.25, alignContent: 'start', minWidth: 0, ...performancePanelSx };
const subheadSx = { ...sectionTitleSx, fontSize: 11.5 };

// A detail fenced off by a change since the grid loaded needs the grid
// refreshed with it; any other failure is retried alone.
function DetailError({ error, label, onRetry, onRefreshGrid }) {
    if (isHistoryContextConflict(error)) {
        return (
            <Alert severity="info" action={<Button size="small" onClick={onRefreshGrid}>Refresh</Button>}>
                {label}’s holdings, prices or factor data changed after the table loaded, so this breakdown would no longer match the number above. Refresh to update both.
            </Alert>
        );
    }
    return (
        <Alert severity="warning" action={<Button size="small" onClick={onRetry}>Retry</Button>}>
            {error?.message || 'This period’s detail couldn’t be loaded.'}
        </Alert>
    );
}

DetailError.propTypes = {
    error: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired,
    onRetry: PropTypes.func.isRequired,
    onRefreshGrid: PropTypes.func.isRequired,
};

/**
 * The selected cell in three panels: the result, what the event is (a
 * standard event's description or your notes) and the factor waterfall
 * behind the result, the day-by-day path with its range band, and the
 * market calendar for those dates. Three across on wide screens, stacked
 * otherwise.
 */
export default function EpisodeDetail({ entry = null, column = null, baselineEntry = null, detail, calendar, refreshing = false, onRefreshGrid }) {
    if (!entry || !column) {
        return <Typography variant="body2" color="text.secondary">Pick a cell in the grid to see how its number is built.</Typography>;
    }
    // Just put in the table: the grid is still replaying it.
    if (column.pending) {
        return <Typography variant="body2" color="text.secondary">The grid is replaying {column.name}; its detail appears once it has.</Typography>;
    }
    const cell = cellOf(entry.row, column);
    const available = Boolean(cell?.available);
    // Asked again after a failure (Refresh or Retry), it shows as loading, not as
    // the old failure; a failure hides a breakdown it may no longer match.
    const loading = available && (refreshing || (detail.isFetching && (!detail.data || detail.isError)));
    const failed = available && !loading && Boolean(detail.error);
    const shown = loading || failed ? null : detail.data;
    const result = shown?.result?.available ? shown.result : cell;
    const windowLabel = formatWindow(column.start_date, column.end_date);
    // A standard event's description, or your notes.
    const about = column.tag === 'yours' ? column.notes : column.description;
    const gap = entry.baseline ? null : cellValue(entry.row, column, 'gap', baselineEntry?.row);
    const notes = cellNotes(cell);
    const steps = shown?.result?.available ? waterfallSteps(shown.by_factor, shown.result) : null;
    const points = shown ? pathPoints(shown.daily_path) : [];
    const summary = pathSummary(points);
    return (
        <Box aria-live="polite" sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            <Paper component="section" aria-label="Result and factor contributions" sx={panelSx}>
                <Box sx={{ display: 'grid', gap: 0.25 }}>
                    <Typography component="h4" sx={{ m: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{column.name} · {entry.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {windowLabel}{available ? ` · ${cell.trading_days} trading day${cell.trading_days === 1 ? '' : 's'}` : ''} · factor replay (estimate)
                    </Typography>
                </Box>
                {about && <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.45 }}>{about}</Typography>}
                {available && (
                    <Typography variant="caption" color="text.secondary">
                        If a period like this happened again, today’s {entry.label} holdings would see
                    </Typography>
                )}
                {available ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.25, rowGap: 0.5 }}>
                        <Typography component="span" sx={{ fontSize: 30, fontWeight: 500, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                            {formatPercent(result.total, 2)}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary">
                            {Number.isFinite(result.specific_range) ? `${formatRange(result.specific_range, 2)} stock-specific range` : 'Stock-specific range unavailable'}
                        </Typography>
                        {gap !== null && <KitChip className="!normal-case" label={`${formatPercent(gap, 2)} vs ${baselineEntry.label}`} />}
                    </Box>
                ) : (
                    <Alert severity="info">{reasonsText(cell?.reasons) || 'No result for this portfolio and period.'}</Alert>
                )}
                {available && Number.isFinite(result.total_usd) && (
                    <Typography variant="caption" color="text.secondary">
                        {formatUsd(result.total_usd)}{Number.isFinite(entry.row?.gross_notional_usd) ? ` on ${formatCompactUsd(entry.row.gross_notional_usd)} opening gross` : ''}
                    </Typography>
                )}
                {notes && <Typography variant="caption" sx={{ color: 'warning.main' }}>{notes}</Typography>}
                {available && <Typography component="h5" sx={{ ...subheadSx, m: 0 }}>Where the result comes from</Typography>}
                {available && loading && <DotWaveLoader height={220} label="Loading factor contributions" />}
                {failed && <DetailError error={detail.error} label={entry.label} onRetry={detail.refetch} onRefreshGrid={onRefreshGrid} />}
                {available && steps && (steps.length > 1
                    ? <FactorWaterfall steps={steps} />
                    : <Typography variant="body2" color="text.secondary">No factor exposure moved this result.</Typography>)}
                {available && steps && steps.length > 1 && (
                    <Typography variant="caption" color="text.secondary">Bars add up factor by factor. The whisker on the total is the stock-specific range.</Typography>
                )}
            </Paper>
            <Paper component="section" aria-label="Day by day" sx={panelSx}>
                <Typography component="h5" sx={{ ...subheadSx, m: 0 }}>Day by day</Typography>
                {available && loading && <DotWaveLoader height={190} label="Loading the daily path" />}
                {available && !loading && points.length > 0 && <ReplayPathChart points={points} />}
                {failed && !points.length && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">The daily path couldn’t be loaded.</Typography>
                        {isHistoryContextConflict(detail.error)
                            ? <Button size="small" onClick={onRefreshGrid}>Refresh</Button>
                            : <Button size="small" onClick={() => { void detail.refetch(); }}>Retry</Button>}
                    </Box>
                )}
                {!loading && !failed && !points.length && (
                    <Typography variant="body2" color="text.secondary">{available ? 'The daily path appears once the detail loads.' : 'There is no daily path for this period.'}</Typography>
                )}
                {summary && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, fontSize: 12.5, color: 'text.secondary' }}>
                        <span>Worst point <Box component="b" sx={{ color: 'text.primary', fontWeight: 500 }}>{formatPercent(summary.worst.cumulative, 2)} on {formatDay(summary.worst.date)}</Box></span>
                        <span>End <Box component="b" sx={{ color: 'text.primary', fontWeight: 500 }}>{formatPercent(summary.end, 2)}</Box></span>
                        {Number.isFinite(summary.rangeAtEnd) && <span>Range at end <Box component="b" sx={{ color: 'text.primary', fontWeight: 500 }}>{formatRange(summary.rangeAtEnd, 2)}</Box></span>}
                    </Box>
                )}
            </Paper>
            <CalendarCard windowLabel={windowLabel} loading={calendar.isLoading} error={calendar.error} calendar={calendar.data || null} onRetry={calendar.refetch} />
        </Box>
    );
}

EpisodeDetail.propTypes = {
    entry: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, baseline: PropTypes.bool, row: PropTypes.object }),
    column: PropTypes.shape({
        id: PropTypes.string, tag: PropTypes.string, name: PropTypes.string, start_date: PropTypes.string, end_date: PropTypes.string,
        description: PropTypes.string, notes: PropTypes.string, pending: PropTypes.bool,
    }),
    baselineEntry: PropTypes.shape({ label: PropTypes.string, row: PropTypes.object }),
    detail: PropTypes.shape({
        data: PropTypes.object, isFetching: PropTypes.bool, isError: PropTypes.bool, error: PropTypes.any, refetch: PropTypes.func,
    }).isRequired,
    calendar: PropTypes.shape({ data: PropTypes.object, isLoading: PropTypes.bool, error: PropTypes.any, refetch: PropTypes.func }).isRequired,
    refreshing: PropTypes.bool,
    onRefreshGrid: PropTypes.func.isRequired,
};
